import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { agentService } from '../services/api';
import { Agent } from '../types';
import { Send, Loader2, Copy, Check, AlertTriangle, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const CORE_API = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';

type EndpointType = 'chat' | 'chat/stream' | 'chat/pro' | 'chat/pro/stream';

const ApiTestingPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [query, setQuery] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [endpoint, setEndpoint] = useState<EndpointType>('chat');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [billing, setBilling] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await agentService.getAll();
      setAgents(data);
      if (data.length > 0) setSelectedAgentId(data[0].id);
    } catch {
      toast.error('Failed to load agents');
    }
  };

  const isStreaming = endpoint === 'chat/stream' || endpoint === 'chat/pro/stream';

  const buildUrl = () => {
    return `${CORE_API}/api/v1/agents/${selectedAgentId}/${endpoint}`;
  };

  const buildCurl = () => {
    const url = buildUrl();
    const headers = `-H "Content-Type: application/json"${apiKey ? ` -H "x-api-key: ${apiKey}"` : ''}`;
    const body = JSON.stringify({ query, userId: 'api-tester' });
    return `curl -X POST ${url} ${headers} -d '${body}'`;
  };

  const handleSend = async () => {
    if (!query.trim() || !selectedAgentId) return;

    setLoading(true);
    setResponse('');
    setBilling(null);
    setResponseTime(null);
    const startTime = Date.now();

    const url = buildUrl();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (apiKey.trim()) headers['x-api-key'] = apiKey.trim();

    try {
      if (isStreaming) {
        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, userId: 'api-tester' }),
        });

        if (!res.ok || !res.body) {
          const text = await res.text();
          setResponse(`Error ${res.status}: ${text}`);
          setLoading(false);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (currentEvent === 'chunk' && data.text) {
                  fullText += data.text;
                  setResponse(fullText);
                } else if (currentEvent === 'done') {
                  setBilling(data.billing || null);
                } else if (currentEvent === 'error') {
                  setResponse(prev => prev + `\n\n[Error: ${data.error}]`);
                }
              } catch {}
            }
          }
        }

        setResponseTime(Date.now() - startTime);
      } else {
        const res = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query, userId: 'api-tester' }),
        });

        const data = await res.json();
        setResponseTime(Date.now() - startTime);

        if (data.success) {
          setResponse(data.response);
          setBilling(data.billing || null);
        } else {
          setResponse(`Error: ${data.error || 'Unknown error'}`);
        }
      }
    } catch (err: any) {
      setResponse(`Request failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, type: 'response' | 'curl') => {
    navigator.clipboard.writeText(text);
    if (type === 'response') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCurlCopied(true);
      setTimeout(() => setCurlCopied(false), 2000);
    }
    toast.success('Copied to clipboard');
  };

  return (
    <div className="flex flex-col h-screen">
      <Header title="API Testing" subtitle="Test the Developer API endpoints directly" />

      <div className="flex-1 overflow-y-auto p-8">
        {/* Cost Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Token Cost Notice</p>
            <p className="text-sm text-amber-700 mt-1">
              API Testing consumes real tokens. <strong>1 Token = 750 words = ₹20</strong> (internal cost).
              Without an API key, queries run in trial mode. With an API key, they bill at ₹50/token.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Request Panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Request</h3>

              {/* Agent Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">Agent</label>
                <div className="relative">
                  <select
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.icon} {a.name} ({a.id})
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Endpoint Select */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">Endpoint</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'chat', label: 'Chat', desc: 'Standard' },
                    { value: 'chat/stream', label: 'Chat Stream', desc: 'SSE' },
                    { value: 'chat/pro', label: 'Pro Chat', desc: 'Enhanced' },
                    { value: 'chat/pro/stream', label: 'Pro Stream', desc: 'Enhanced SSE' },
                  ] as { value: EndpointType; label: string; desc: string }[]).map((ep) => (
                    <button
                      key={ep.value}
                      onClick={() => setEndpoint(ep.value)}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors text-left ${
                        endpoint === ep.value
                          ? 'border-primary bg-primary/5 text-primary font-medium'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">{ep.label}</div>
                      <div className="text-xs opacity-60">{ep.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* API Key (optional) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  API Key <span className="text-gray-400 font-normal">(optional — leave empty for trial mode)</span>
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="x-api-key header value"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {/* Query */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-1">Query</label>
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your legal query here..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
                  }}
                />
              </div>

              {/* Send Button */}
              <button
                onClick={handleSend}
                disabled={loading || !query.trim() || !selectedAgentId}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {isStreaming ? 'Streaming...' : 'Sending...'}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Request
                  </>
                )}
              </button>
            </div>

            {/* cURL Preview */}
            <div className="bg-gray-900 rounded-xl p-4 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-400 uppercase">cURL Command</span>
                <button
                  onClick={() => copyToClipboard(buildCurl(), 'curl')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {curlCopied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <pre className="text-xs text-green-400 overflow-x-auto whitespace-pre-wrap break-all font-mono">
                {selectedAgentId ? buildCurl() : 'Select an agent to see the cURL command'}
              </pre>
            </div>

            {/* Endpoint Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex justify-between">
                  <span>Method</span>
                  <span className="font-mono font-medium text-blue-600">POST</span>
                </div>
                <div className="flex justify-between">
                  <span>URL</span>
                  <span className="font-mono text-gray-700 text-right break-all">{selectedAgentId ? buildUrl() : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Mode</span>
                  <span className={`font-medium ${apiKey ? 'text-green-600' : 'text-orange-600'}`}>
                    {apiKey ? 'Production (₹50/token)' : 'Trial (₹20/token)'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Response Panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6 min-h-[400px] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Response</h3>
                <div className="flex items-center gap-3">
                  {responseTime !== null && (
                    <span className="text-xs text-gray-400">{responseTime}ms</span>
                  )}
                  {response && (
                    <button
                      onClick={() => copyToClipboard(response, 'response')}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading && !response ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 size={24} className="animate-spin text-primary" />
                  </div>
                ) : response ? (
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                    {response}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    Response will appear here
                  </div>
                )}
              </div>
            </div>

            {/* Billing Info */}
            {billing && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Billing</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {billing.tokensCharged !== undefined && (
                    <div>
                      <span className="text-gray-500">Tokens Charged</span>
                      <div className="font-medium text-gray-900">{billing.tokensCharged?.toFixed(4)}</div>
                    </div>
                  )}
                  {billing.tokensRemaining !== undefined && billing.tokensRemaining !== null && (
                    <div>
                      <span className="text-gray-500">Tokens Remaining</span>
                      <div className="font-medium text-gray-900">{billing.tokensRemaining?.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                    </div>
                  )}
                  {billing.costINR !== undefined && (
                    <div>
                      <span className="text-gray-500">Cost</span>
                      <div className="font-medium text-red-600">₹{billing.costINR?.toFixed(2)}</div>
                    </div>
                  )}
                  {billing.wordsGenerated !== undefined && (
                    <div>
                      <span className="text-gray-500">Words Generated</span>
                      <div className="font-medium text-gray-900">{billing.wordsGenerated}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTestingPage;
